// src/pages/ViewAsCustomerPage.jsx
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  HStack,
  VStack,
  Badge,
  Divider,
  useDisclosure,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputRightElement,
  Tag,
  TagLabel,
  TagLeftIcon,
  Flex,
} from "@chakra-ui/react";
import {
  ExternalLinkIcon,
  RepeatIcon,
  CopyIcon,
  CloseIcon,
  TimeIcon,
  WarningIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";
import axios from "axios";

import UserPickerDrawer from "components/UserPickerDrawer";
import { useCognito } from "../utils/useCognito";

// ====== CONFIG ======
const ADMIN_API_BASE =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api";

// You created this lambda + endpoint:
const API_CREATE_SUPPORT_SESSION = `${ADMIN_API_BASE}/createSupportSession`;

// Where the customer portal lives:
// const CUSTOMER_PORTAL_BASE_URL = "https://www.curagenesis.com/portal/dashboard";
const CUSTOMER_PORTAL_BASE_URL = "http://localhost:3000/portal/dashboard";


// Route we’ll add on the customer portal:
const CUSTOMER_SUPPORT_ROUTE = "/support";

// Use hash so it doesn’t land in server logs as query params.
// (Still visible to the client, which is okay — token is short-lived.)
const buildCustomerSupportUrl = (supportToken) =>
  `${CUSTOMER_PORTAL_BASE_URL}${CUSTOMER_SUPPORT_ROUTE}#token=${encodeURIComponent(
    supportToken
  )}`;

// Small helper: decode JWT payload without verifying (UI only)
function safeDecodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function formatMs(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export default function ViewAsCustomerPage() {
  const toast = useToast();
  const { getIdToken } = useCognito();
  const picker = useDisclosure();

  const [selectedUser, setSelectedUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const [supportToken, setSupportToken] = useState("");
  const [supportUrl, setSupportUrl] = useState("");

  // session meta (derived from token)
  const [expiresAtMs, setExpiresAtMs] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  // open tab tracking
  const tabRef = useRef(null);
  const [tabStatus, setTabStatus] = useState("idle"); // idle | opened | closed | unknown
  const [lastPingAt, setLastPingAt] = useState(null);

  // BroadcastChannel lets the new tab communicate back (once customer /support route exists).
  // For now, we’ll wire listener on admin side; customer side will be added later.
  const channelRef = useRef(null);

  // For a visible “Admin X” label (optional)
  const adminLabel = "Admin User";

  const yellowBand = useMemo(() => {
    if (!selectedUser) return null;
    return {
      email: selectedUser.email || "(no email)",
      userId: selectedUser.UserId || "(no userId)",
    };
  }, [selectedUser]);

  const copyToClipboard = useCallback(
    async (text, label = "Copied") => {
      try {
        await navigator.clipboard.writeText(text);
        toast({ title: label, status: "success", duration: 1800, isClosable: true });
      } catch (err) {
        console.error("Clipboard error:", err);
        toast({
          title: "Copy failed",
          description: "Your browser blocked clipboard access.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [toast]
  );

  const reset = useCallback(() => {
    setSelectedUser(null);
    setSupportToken("");
    setSupportUrl("");
    setExpiresAtMs(null);
    setSecondsLeft(null);
    setTabStatus("idle");
    setLastPingAt(null);
    tabRef.current = null;
  }, []);

  const handleSelectUser = useCallback(
    (user) => {
      setSelectedUser(user);
      picker.onClose();
      // Reset prior session state
      setSupportToken("");
      setSupportUrl("");
      setExpiresAtMs(null);
      setSecondsLeft(null);
      setTabStatus("idle");
      setLastPingAt(null);
      tabRef.current = null;
    },
    [picker]
  );

  const createSession = useCallback(async () => {
    if (!selectedUser?.UserId) {
      toast({
        title: "Pick a user first",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsCreating(true);
    try {
      const token = getIdToken();

      const res = await axios.post(
        API_CREATE_SUPPORT_SESSION,
        {
          targetUserId: selectedUser.UserId,
          targetEmail: selectedUser.email,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const newToken = res.data?.supportToken;
      if (!newToken) throw new Error("Missing supportToken in response");

      const url = buildCustomerSupportUrl(newToken);

      // derive expiry from token
      const payload = safeDecodeJwtPayload(newToken);
      const expSec = payload?.exp ? Number(payload.exp) : null;
      const expMs = expSec ? expSec * 1000 : null;

      setSupportToken(newToken);
      setSupportUrl(url);
      setExpiresAtMs(expMs || null);

      // reset tab tracking on new session
      setTabStatus("idle");
      setLastPingAt(null);
      tabRef.current = null;

      toast({
        title: "Support session created",
        description: "Open it in a new tab to view the customer portal as that user (read-only).",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (err) {
      console.error("createSupportSession error:", err);
      toast({
        title: "Failed to create support session",
        description:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          "Unknown error",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  }, [selectedUser, getIdToken, toast]);

  const openInNewTab = useCallback(() => {
    if (!supportUrl) return;

    // open new tab and store a ref
    const win = window.open(supportUrl, "_blank", "noopener,noreferrer");
    tabRef.current = win;

    setTabStatus("opened");
    toast({
      title: "Opened support view",
      description: "A read-only support session tab was opened.",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  }, [supportUrl, toast]);

  // ====== Expiry countdown ======
  useEffect(() => {
    if (!expiresAtMs) {
      setSecondsLeft(null);
      return;
    }

    const tick = () => {
      const msLeft = expiresAtMs - Date.now();
      setSecondsLeft(Math.max(0, Math.floor(msLeft / 1000)));
    };

    tick();
    const i = setInterval(tick, 250);
    return () => clearInterval(i);
  }, [expiresAtMs]);

  const isExpired = useMemo(() => {
    if (!expiresAtMs) return false;
    return Date.now() >= expiresAtMs;
  }, [expiresAtMs]);

  // ====== Optional: detect tab close (best-effort only) ======
  // If we have a window ref, polling "closed" is allowed (limited, but works).
  useEffect(() => {
    if (!supportUrl) return;

    const i = setInterval(() => {
      const w = tabRef.current;
      if (!w) return;

      // if tab was opened and now closed
      if (tabStatus === "opened" && w.closed) {
        setTabStatus("closed");
        toast({
          title: "Support tab closed",
          description: "The support session tab was closed. The token may still be valid until expiry.",
          status: "warning",
          duration: 3500,
          isClosable: true,
        });
      }
    }, 800);

    return () => clearInterval(i);
  }, [supportUrl, tabStatus, toast]);

  // ====== Optional: cross-tab pings (requires customer /support route JS later) ======
  // We'll listen now; later the customer portal can ping us using BroadcastChannel("cg-support-session")
  useEffect(() => {
    try {
      const bc = new BroadcastChannel("cg-support-session");
      channelRef.current = bc;

      bc.onmessage = (evt) => {
        const msg = evt?.data;
        if (!msg || typeof msg !== "object") return;

        // Basic shape we’ll support later:
        // { type: "PING", sessionId }
        // { type: "CLOSE", sessionId }
        // { type: "READY", sessionId }
        if (msg.type === "PING") {
          setLastPingAt(Date.now());
          if (tabStatus === "opened" || tabStatus === "idle") setTabStatus("opened");
        }
        if (msg.type === "CLOSE") {
          setTabStatus("closed");
          toast({
            title: "Support view ended",
            description: "Customer support tab signaled it’s closing.",
            status: "info",
            duration: 2500,
            isClosable: true,
          });
        }
      };

      return () => {
        bc.close();
        channelRef.current = null;
      };
    } catch (e) {
      // BroadcastChannel not supported (rare)
      console.warn("BroadcastChannel not available:", e);
      return undefined;
    }
  }, [tabStatus, toast]);

  const sessionBadge = useMemo(() => {
    if (!supportToken) return { color: "gray", label: "No Session" };
    if (isExpired) return { color: "red", label: "Expired" };
    return { color: "green", label: "Active" };
  }, [supportToken, isExpired]);

  const tabBadge = useMemo(() => {
    if (!supportUrl) return { color: "gray", label: "No Tab" };
    if (tabStatus === "opened") return { color: "blue", label: "Tab Opened" };
    if (tabStatus === "closed") return { color: "yellow", label: "Tab Closed" };
    if (tabStatus === "unknown") return { color: "orange", label: "Unknown" };
    return { color: "gray", label: "Not Opened" };
  }, [supportUrl, tabStatus]);

  const expiresAtLabel = useMemo(() => {
    if (!expiresAtMs) return "Unknown";
    return new Date(expiresAtMs).toLocaleString();
  }, [expiresAtMs]);

  return (
    <Box p={6}>
      <HStack justify="space-between" align="flex-start" mb={6}>
        <Box>
          <Heading size="xl">View As Customer</Heading>
          <Text mt={2} color="gray.500">
            Create a short-lived support session and open the customer portal in a new tab.
            The portal should run in <b>read-only</b> support mode.
          </Text>
        </Box>

        <HStack>
          <Button
            onClick={picker.onOpen}
            colorScheme="teal"
            variant="solid"
            _hover={{ transform: "scale(1)" }}
            transition="transform 0.12s ease-out"
          >
            Pick User
          </Button>
          <Button
            onClick={reset}
            variant="outline"
            leftIcon={<CloseIcon />}
            isDisabled={!selectedUser && !supportToken}
            _hover={{ transform: "scale(1)" }}
            transition="transform 0.12s ease-out"
          >
            Clear
          </Button>
        </HStack>
      </HStack>

      {/* Yellow band */}
      {yellowBand && (
        <Card
          mb={6}
          borderRadius="xl"
          borderWidth="1px"
          borderColor="yellow.300"
          bg="yellow.50"
        >
          <CardBody>
            <HStack justify="space-between" align="flex-start">
              <Box>
                <HStack spacing={3} mb={1}>
                  <Badge colorScheme="yellow" variant="solid">
                    SUPPORT MODE
                  </Badge>
                  <Badge colorScheme="red" variant="subtle">
                    READ-ONLY
                  </Badge>
                </HStack>
                <Text fontWeight="semibold">
                  {adminLabel} viewing on behalf of{" "}
                  <Text as="span" fontFamily="mono">
                    {yellowBand.email}
                  </Text>
                </Text>
                <Text fontSize="sm" color="gray.600">
                  User ID:{" "}
                  <Text as="span" fontFamily="mono" color="gray.800">
                    {yellowBand.userId}
                  </Text>
                </Text>
              </Box>

              <HStack>
                <Tooltip label="Create/refresh support session">
                  <IconButton
                    aria-label="Refresh support session"
                    icon={isCreating ? <Spinner size="sm" /> : <RepeatIcon />}
                    onClick={createSession}
                    isDisabled={!selectedUser?.UserId || isCreating}
                    variant="outline"
                    borderRadius="lg"
                    _hover={{ transform: "scale(1)" }}
                    transition="transform 0.12s ease-out"
                  />
                </Tooltip>
              </HStack>
            </HStack>
          </CardBody>
        </Card>
      )}

      {/* Primary action card */}
      <Card borderRadius="xl" shadow="md" variant="outline">
        <CardHeader>
          <HStack justify="space-between" align="center">
            <Heading size="md">Session</Heading>
            <Badge colorScheme={sessionBadge.color} variant="subtle">
              {sessionBadge.label}
            </Badge>
          </HStack>
        </CardHeader>

        <CardBody>
          <VStack align="stretch" spacing={4}>
            {!selectedUser ? (
              <Alert status="info" borderRadius="lg">
                <AlertIcon />
                <Box>
                  <AlertTitle>Pick a user to start</AlertTitle>
                  <AlertDescription>
                    Choose the account you want to view, then generate a support session token.
                  </AlertDescription>
                </Box>
              </Alert>
            ) : (
              <VStack spacing={4} align="stretch">
                <HStack spacing={3} flexWrap="wrap">
                  <Button
                    onClick={createSession}
                    colorScheme="teal"
                    isLoading={isCreating}
                    loadingText="Creating…"
                    _hover={{ transform: "scale(1)" }}
                    transition="transform 0.12s ease-out"
                  >
                    Create Support Session
                  </Button>

                  <Button
                    onClick={() => copyToClipboard(supportUrl, "Support link copied")}
                    leftIcon={<CopyIcon />}
                    variant="ghost"
                    isDisabled={!supportUrl}
                    _hover={{ transform: "scale(1)" }}
                    transition="transform 0.12s ease-out"
                  >
                    Copy Link
                  </Button>
                </HStack>

                {supportUrl && !isExpired && (
                  <Alert status="success" borderRadius="lg" variant="left-accent">
                    <VStack spacing={4} align="stretch" width="100%">
                      <HStack>
                        <AlertIcon />
                        <Box>
                          <AlertTitle>Session ready!</AlertTitle>
                          <AlertDescription>
                            Click below to open the customer portal as this user.
                          </AlertDescription>
                        </Box>
                      </HStack>
                      <Flex justify="center">
                        <Button
                          onClick={openInNewTab}
                          leftIcon={<ExternalLinkIcon />}
                          colorScheme="green"
                          size="lg"
                          _hover={{ transform: "scale(1.05)" }}
                          transition="transform 0.12s ease-out"
                        >
                          Open Support View
                        </Button>
                      </Flex>
                    </VStack>
                  </Alert>
                )}
              </VStack>
            )}

            {supportUrl && (
              <>
                <Divider />

                <Box>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Support URL
                  </Text>
                  <InputGroup>
                    <Input value={supportUrl} isReadOnly borderRadius="xl" />
                    <InputRightElement>
                      <Tooltip label="Copy URL">
                        <IconButton
                          aria-label="Copy URL"
                          icon={<CopyIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(supportUrl, "URL copied")}
                          _hover={{ transform: "scale(1)" }}
                          transition="transform 0.12s ease-out"
                        />
                      </Tooltip>
                    </InputRightElement>
                  </InputGroup>

                  <HStack mt={3} spacing={3} flexWrap="wrap">
                    <Tag colorScheme={isExpired ? "red" : "green"} variant="subtle">
                      <TagLeftIcon as={isExpired ? WarningIcon : CheckCircleIcon} />
                      <TagLabel>
                        {isExpired ? "Expired" : "Expires"}: {expiresAtLabel}
                      </TagLabel>
                    </Tag>

                    <Tag colorScheme={isExpired ? "red" : "blue"} variant="subtle">
                      <TagLeftIcon as={TimeIcon} />
                      <TagLabel>
                        Time left:{" "}
                        {secondsLeft == null ? "Unknown" : formatMs(secondsLeft * 1000)}
                      </TagLabel>
                    </Tag>

                    <Badge colorScheme={tabBadge.color} variant="subtle">
                      {tabBadge.label}
                    </Badge>

                    {lastPingAt && (
                      <Badge colorScheme="purple" variant="subtle">
                        Last ping: {new Date(lastPingAt).toLocaleTimeString()}
                      </Badge>
                    )}
                  </HStack>

                  {isExpired && (
                    <Alert status="error" borderRadius="lg" mt={4}>
                      <AlertIcon />
                      <Box>
                        <AlertTitle>Session expired</AlertTitle>
                        <AlertDescription>
                          Create a new support session to continue viewing as this user.
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}

                </Box>
              </>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* User picker */}
      <UserPickerDrawer
        isOpen={picker.isOpen}
        onClose={picker.onClose}
        onSelectUser={handleSelectUser}
      />
    </Box>
  );
}
